import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Table,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Avatar,
  TableContainer,
  styled,
  Box,
  useTheme,
  Stack,
  Typography,
  Button,
  capitalize,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import { IObject } from 'src/models/objectManagement';
import moment from 'moment';
// import YouTubeIcon from '@mui/icons-material/YouTube';
// import TwitterIcon from '@mui/icons-material/Twitter';
// import InstagramIcon from '@mui/icons-material/Instagram';
// import FacebookIcon from '@mui/icons-material/Facebook';
// import { TSocialMedia } from 'src/models/general';
import DeleteIcon from '@mui/icons-material/Delete';
import { addSymbolObject } from 'src/utils/addSymbolObject';
import useStyles from './styles';
import { useTable } from 'src/hooks/useTable';
import HeaderObjects from 'src/content/settings/MonitoredAssets/components/HeaderObjects';
// import TiktokIcon from 'src/components/TikTokIcon/TikTokIcon';
// import LinkedInIcon from '@mui/icons-material/LinkedIn';
// import { themeColors } from 'src/theme/schemes/PureLightTheme';
import TableHeaderObjects from './TableHeaderObjects';
import GetSocialMediaIconWithBgColor from 'src/components/GetSocialMediaIconWithBgColor';
// import TelegramIcon from '@mui/icons-material/Telegram';
// import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useAlert } from 'src/hooks/useAlert';
import useObjectManagement from 'src/services/useObjectManagement';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { useApolloClient, useMutation } from '@apollo/client';
import {
  GET_OBJECTS_WITH_ENTITY,
  GET_OBJECTS_WITH_STATUS
} from 'src/services/graphql/objectManagement/query';
import Confirmation from 'src/components/Confirmation';
import { getStatusColor } from 'src/utils/getStatusColor';
import NotAllowedTooltip from 'src/components/NotAllowedTooltip';
import { themeColors } from 'src/theme/schemes/PureLightTheme';
import { TSocialMedia } from 'src/models/general';
import { Cached } from '@mui/icons-material';
import { OBJECT_MANAGEMENT_RETRY_OBJECT } from 'src/services/graphql/objectManagement/mutation';
import ObjectDetailPopup from './ObjectDetailPopup';
import { reducerUpdateObjectList } from 'src/features/objectManagement';
// import set from 'date-fns/set/index';

interface Props {
  dataObject: Array<IObject>;
  onEdit: (object: any) => void;
  onDelete: (id: string) => void;
  loading?: boolean;
  height?: number;
  objectLength?: any;
}

interface Item {
  name: string;
}

export const CustomButton = styled(Button)(
  ({ theme }) => `
        min-width: unset;
        max-height: ${theme.spacing(3)};
        font-style: normal;
        font-weight: 500;
        font-size: 13px;
        border-radius: 8px;
  `
);

export const FabSmall = styled(Fab)(
  ({ theme, disabled }) => `
    min-height: unset;
    background-color: transparent;
    width: ${theme.spacing(3)};
    height: ${theme.spacing(3)};
    color: #E35200;
    border: 1px solid ${disabled ? '#051c2605' : '#E35200'};
  `
);

const integrationUrl = (socialMedia: TSocialMedia) => {
  return `https://external.backend.dashboard.nolimit.id/social-media/${socialMedia}/account/authorize/auth-url`;
};

const TableObjects: React.FC<Props> = ({
  dataObject,
  loading = false,
  height,
  onDelete,
  onEdit,
  objectLength
}) => {
  const { handleClickAlert } = useAlert();
  const addName = useMemo((): Array<IObject & Item> => {
    return dataObject.map((object) => ({
      ...object,
      name: object.objectName
    }));
  }, [dataObject]);

  const {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    dataListByPage,
    handleSearch,
    filteredList,
    handleRequestSort,
    orderBy,
    order
  } = useTable<IObject>(addName);

  const optionLimits = [10, 25, 50, 100];
  const handleChangeLimit = (event) => {
    const handleLimit = parseInt(event.target.value);
    handleChangeRowsPerPage(handleLimit);
  };
  const theme = useTheme();
  const classes = useStyles();
  const { disconnectObject } = useObjectManagement();
  const client = useApolloClient();
  const {
    id: projectId,
    role: userRole,
    customerType
  } = useAppSelector((state) => state.users.project);
  const token = localStorage.getItem('token');
  const [integratingObject, setIntegratingObject] = useState(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [objectId, setObjectId] = useState<string | null>(null);
  const [openObjectPopup, setOpenObjectPopup] = useState<boolean>(false);
  const [selectedObject, setSelectedObject] = useState<IObject | null>(null);

  const { objectList } = useAppSelector((state) => state.objectManagement);
  const dispatch = useAppDispatch();

  const isRetail = customerType === 'b2c';
  const isMember = userRole === 'member';

  const canBeIntegrated = (object: IObject) => {
    return (
      (object.socialMedia === 'tiktok' || object.socialMedia === 'instagram') &&
      !object.isEntity &&
      object.streamType === 'account'
    );
  };

  const handleClickDisconnect = (id: string) => {
    setOpenConfirm(true);
    setObjectId(id);
  };

  const handleIntegrateObject = async (object: IObject) => {
    setIntegratingObject(true);
    setObjectId(object.id);
    try {
      const params = new URLSearchParams();
      params.append('objectId', object.id);

      const response = await fetch(
        `${integrationUrl(object.socialMedia)}?${params}`,
        {
          method: 'GET',
          headers: new Headers({
            'Content-Type': 'application/json;charset=UTF-8',
            'Access-Control-Allow-Origin': '*',
            'X-JWT-Token': token ? token : ''
          })
        }
      );
      const res = await response.json();
      window.location.href = res.auth_url;
    } catch (error) {
      handleClickAlert({
        horizontal: 'center',
        vertical: 'top',
        message: 'Error integrating to object',
        severity: 'error'
      });
    } finally {
      setIntegratingObject(false);
      setObjectId('');
    }
  };

  const handleDisconnectObject = async (objectId: string) => {
    setIntegratingObject(true);
    setObjectId(objectId);
    try {
      await disconnectObject({
        variables: { objectId: objectId, projectId: projectId },
        onCompleted: () => {
          dispatch(
            reducerUpdateObjectList({
              ...objectList,
              data: objectList.data.map((item) => {
                return {
                  ...item,
                  isAuthorizedAccount:
                    item.id === objectId ? false : item.isAuthorizedAccount
                };
              })
            })
          );
        }
      });
      client.refetchQueries({
        include: [GET_OBJECTS_WITH_ENTITY, GET_OBJECTS_WITH_STATUS]
      });
    } catch (error) {
      handleClickAlert({
        horizontal: 'center',
        vertical: 'top',
        message: 'Error disconnecting to object, please try again',
        severity: 'error'
      });
    } finally {
      setIntegratingObject(false);
      setObjectId('');
    }
  };

  const getStatusMessage = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'The object has been created successfully and is currently queued for processing.';
      case 'running':
        return 'The system is currently collecting and processing data for this object. Please wait until the process is completed.';
      case 'error':
        return 'This object could not be processed due to an issue with the provided data or source. Notes: please delete this object and add it again. Make sure the username or source link is correct, valid, and supported by the system before re-adding.';
      case 'timeout':
        return 'The process could not be completed because the system did not receive a response within the expected time limit. Notes: please delete this object and add it again. Make sure the username or source link is correct, valid, and supported by the system before re-adding.';
      default:
        return '';
    }
  };

  // reset page to 1 when object filter occurs
  useEffect(() => {
    handleChangePage(1);
  }, [dataObject]);

  const [retryObject, resRetryObject] = useMutation(
    OBJECT_MANAGEMENT_RETRY_OBJECT
  );

  const handleRetryObject = (objectId: string) => {
    retryObject({
      variables: {
        objectId
      },
      onCompleted: () => {
        client.refetchQueries({
          include: [GET_OBJECTS_WITH_STATUS]
        });
      }
    })
      .then((res) => {
        if (!res) {
          handleClickAlert({
            horizontal: 'center',
            vertical: 'top',
            message: 'Error retrying object',
            severity: 'error'
          });
          return;
        }
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Object has been successfully retried',
          severity: 'success'
        });
      })
      .catch(() => {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Error retrying object',
          severity: 'error'
        });
      })
      .finally(() => {
        setObjectId('');
      });
  };

  return (
    <>
      <HeaderObjects
        objectList={dataObject}
        objectCount={objectLength}
        onSearch={handleSearch}
      />
      <Box sx={{ marginTop: 2 }}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="medium" aria-label="simple table">
            <TableHeaderObjects
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={addName.length}
            />
            <TableBody>
              {dataListByPage.length > 0 ? (
                dataListByPage.map((row, index) => (
                  <TableRow key={index} sx={{ height: 64 }}>
                    <TableCell align="center" size="small" width="5%">
                      {(page - 1) * rowsPerPage + (index + 1)}
                    </TableCell>
                    <TableCell
                      align="left"
                      size="small"
                      width="25%"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        setOpenObjectPopup(true);
                        setSelectedObject(row);
                      }}
                    >
                      <Stack spacing={1} direction="row" alignItems="center">
                        <Box>
                          <GetSocialMediaIconWithBgColor
                            socialMedia={row.socialMedia}
                          />
                        </Box>
                        {row.status === 'success' ? (
                          <Typography variant="h4" sx={{ fontSize: 13 }}>
                            {row.streamType === 'post_link'
                              ? row.objectName
                                  .replace('https://', '')
                                  .replace('www.', '')
                              : addSymbolObject(row).objectName}
                          </Typography>
                        ) : (
                          <Typography variant="h4" sx={{ fontSize: 13 }}>
                            {row.streamType === 'post_link'
                              ? row.objectName
                                  .replace('https://', '')
                                  .replace('www.', '')
                              : row.objectName}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center" size="small" width="30%">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 500,
                            fontSize: 13
                          }}
                        >
                          {moment(row.createdAt).format('ddd, MMM DD YYYY')}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 300,
                            fontSize: 13
                          }}
                        >
                          {moment(row.createdAt).format(', HH:mm')}
                        </Typography>
                      </Box>
                    </TableCell>
                    {isRetail && (
                      <TableCell align="center" size="small" width="30%">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          {row.lastDataUpdated ? (
                            <>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: 13
                                }}
                              >
                                {moment(row.lastDataUpdated).format(
                                  'ddd, MMM DD YYYY'
                                )}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 300,
                                  fontSize: 13
                                }}
                              >
                                {moment(row.lastDataUpdated).format(', HH:mm')}
                              </Typography>
                            </>
                          ) : (
                            <Chip
                              size="small"
                              label="On Progress"
                              sx={{
                                fontWeight: 400,
                                backgroundColor: themeColors.blue,
                                color: 'white'
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    )}
                    <TableCell align="center" size="small" width="10%">
                      <Typography variant="h4" sx={{ fontSize: 13 }}>
                        {row.streamType === 'post_link'
                          ? 'Post Link'
                          : row.streamType === 'keyword' && row.isHashtag
                          ? 'Hashtag'
                          : capitalize(row.streamType)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" size="small" width="5%">
                      {row.entityName ?? '-'}
                    </TableCell>
                    <TableCell align="center" size="small" width="5%">
                      <Tooltip
                        title={
                          row.status === 'error'
                            ? row.errorMessage
                            : getStatusMessage(row.status)
                        }
                        placement="top-start"
                      >
                        <Chip
                          size="small"
                          label={capitalize(row.status)}
                          sx={{
                            fontWeight: 700,
                            backgroundColor: `${getStatusColor(row.status)}30`,
                            color: getStatusColor(row.status)
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" width="25%">
                      <Stack
                        direction="row"
                        gap={1}
                        alignItems="center"
                        justifyContent="end"
                      >
                        {row.status === 'error' ? (
                          <CustomButton
                            size="small"
                            disabled={
                              objectId === row.id && resRetryObject.loading
                            }
                            onClick={() => {
                              setObjectId(row.id);
                              handleRetryObject(row.id);
                            }}
                            sx={{
                              backgroundColor: `${themeColors.error}30`,
                              color: themeColors.error,
                              ':hover': {
                                opacity: 0.5
                              },
                              fontWeight: 700,
                              border: `1px solid ${themeColors.error}`,
                              ':disabled': {
                                opacity: 0.5
                              }
                            }}
                            startIcon={<Cached />}
                          >
                            {objectId === row.id && resRetryObject.loading
                              ? 'Retrying...'
                              : 'Retry'}
                          </CustomButton>
                        ) : null}
                        {isMember ? (
                          <NotAllowedTooltip>
                            <CustomButton
                              disabled={
                                row.streamType !== 'keyword' || row.isEntity
                              }
                              size="small"
                              variant="contained"
                              color="primary"
                            >
                              Edit
                            </CustomButton>
                          </NotAllowedTooltip>
                        ) : (
                          <CustomButton
                            disabled={
                              row.streamType !== 'keyword' || row.isEntity
                            }
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => onEdit(row)}
                          >
                            Edit
                          </CustomButton>
                        )}
                        {canBeIntegrated(row) ? (
                          isMember ? (
                            <NotAllowedTooltip>
                              <IconButton
                                aria-label="integrate"
                                size="small"
                                disabled={integratingObject}
                              >
                                {integratingObject && row.id === objectId ? (
                                  <CircularProgress size={16} />
                                ) : !row.isAuthorizedAccount ? (
                                  <LinkIcon
                                    fontSize="small"
                                    color={'success'}
                                  />
                                ) : (
                                  <LinkOffIcon
                                    fontSize="small"
                                    color={'error'}
                                  />
                                )}
                              </IconButton>
                            </NotAllowedTooltip>
                          ) : (
                            <IconButton
                              aria-label="integrate"
                              size="small"
                              onClick={() =>
                                !row.isAuthorizedAccount
                                  ? handleIntegrateObject(row)
                                  : handleClickDisconnect(row.id)
                              }
                              disabled={integratingObject}
                            >
                              {integratingObject && row.id === objectId ? (
                                <CircularProgress size={16} />
                              ) : !row.isAuthorizedAccount ? (
                                <LinkIcon fontSize="small" color={'success'} />
                              ) : (
                                <LinkOffIcon fontSize="small" color={'error'} />
                              )}
                            </IconButton>
                          )
                        ) : null}
                        {isMember ? (
                          <NotAllowedTooltip>
                            <FabSmall size="small" disabled={row.isEntity}>
                              <DeleteIcon
                                sx={{
                                  fontSize: theme.spacing(1.5)
                                }}
                              />
                            </FabSmall>
                          </NotAllowedTooltip>
                        ) : (
                          <FabSmall
                            size="small"
                            onClick={() => onDelete(row.id)}
                            disabled={row.isEntity}
                          >
                            <DeleteIcon
                              sx={{
                                fontSize: theme.spacing(1.5)
                              }}
                            />
                          </FabSmall>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow hover={false}>
                  <TableCell
                    align="center"
                    colSpan={10}
                    sx={height && { height: theme.spacing(height) }}
                  >
                    {loading ? (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <CircularProgress disableShrink size={64} />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <Avatar>N/A</Avatar>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              )}
              <TableRow hover={false}>
                <TableCell colSpan={12}>
                  <div className={classes.pagination}>
                    <Stack spacing={{ xs: 2, md: 40 }} direction="row">
                      <FormControl
                        variant="outlined"
                        className={classes.formControl}
                        size="small"
                      >
                        <InputLabel id="select-limit-label">Limit</InputLabel>
                        <Select
                          labelId="select-limit-label"
                          id="select-limit"
                          value={rowsPerPage}
                          onChange={handleChangeLimit}
                          label="Limit"
                        >
                          {optionLimits.map((limit) => (
                            <MenuItem value={limit} key={limit}>
                              {limit}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Pagination
                        count={Math.ceil(filteredList.length / rowsPerPage)}
                        onChange={(_, page) => handleChangePage(page)}
                        page={page}
                        shape="rounded"
                        color="primary"
                        size="large"
                      />
                    </Stack>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Confirmation
        onClose={() => setOpenConfirm(false)}
        onOk={() => handleDisconnectObject(objectId)}
        open={openConfirm}
        labelButton="Disconnect"
        title="Disconnect Object Account"
        message="Are you sure you want to disconnect your Object account?"
        loading={integratingObject}
      />
      {openObjectPopup && selectedObject && (
        <ObjectDetailPopup
          open={openObjectPopup}
          handleClose={() => setOpenObjectPopup(false)}
          object={selectedObject}
        />
      )}
    </>
  );
};

export default TableObjects;
